// Personal Slack DMs (ADR-0077 decision 11; registry integrations.slack.personal-notifications). Isomorphic and pure:
// the web channel page writes these preferences and the server's slack.dm job reads them.
//
// Storage, in the user's own UserSettings row (no new table):
//   notification_channel_prefs.slack                 the master switch; absent ⇒ false (DMs are opt-in)
//   notification_category_prefs.<category>.slack      the category × channel matrix cell; absent ⇒ true (every
//                                                     category is on once the channel is, as for email)
//   notification_category_prefs.slackDmStopped        why the server turned DMs off (a terminal Slack error), or
//                                                     absent; the channel page shows it and turning DMs on clears it
import { NOTIFICATION_TYPES, type NotificationCategory } from '../inbox/notify/catalog';

const LABELS: Readonly<Partial<Record<NotificationCategory, string>>> = {
  assignments: 'Assignments',
  statusChanges: 'Status changes',
  commentsAndReplies: 'Comments and replies',
  mentions: 'Mentions',
  reactions: 'Reactions',
  subscriptions: 'Subscriptions',
  documentChanges: 'Document changes',
  postsAndUpdates: 'Posts and updates',
  reminders: 'Reminders',
};

/** The categories Quadratic emits, in the channel page's order (Linear's documented four first). */
export const SLACK_DM_CATEGORIES: readonly { id: NotificationCategory; label: string }[] = (
  Object.keys(LABELS) as NotificationCategory[]
)
  .filter((c) => NOTIFICATION_TYPES.some((t) => t.category === c))
  .map((id) => ({ id, label: LABELS[id]! }));

export const SLACK_DM_STOPPED_KEY = 'slackDmStopped';

/** Slack errors on a DM that stop the recipient's DMs (theirs, not the installation's). */
export const SLACK_DM_STOP_CODES = ['account_inactive', 'user_not_found', 'user_disabled', 'cannot_dm_bot'] as const;
export type SlackDmStopCode = (typeof SLACK_DM_STOP_CODES)[number];

const STOP_TEXT: Readonly<Record<SlackDmStopCode, string>> = {
  account_inactive: 'Slack DMs stopped: your Slack account is deactivated',
  user_disabled: 'Slack DMs stopped: your Slack account is deactivated',
  user_not_found: 'Slack DMs stopped: your Slack account was not found',
  cannot_dm_bot: 'Slack DMs stopped: Slack refused the message',
};

export const SLACK_DM_TEXT = {
  notLinked: 'Not linked: type /quadratic link in Slack',
  notConfigured: 'Slack isn’t connected to this workspace',
  linked: (name: string | null): string => (name ? `Linked as @${name}` : 'Linked'),
} as const;

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

export function slackDmOn(channelPrefs: unknown): boolean {
  return obj(channelPrefs).slack === true;
}

export function slackCategoryOn(categoryPrefs: unknown, category: string): boolean {
  return obj(obj(categoryPrefs)[category]).slack !== false;
}

/** The prefs with one matrix cell set (the other channels of that category kept). */
export function withSlackCategory(categoryPrefs: unknown, category: string, on: boolean): Record<string, unknown> {
  const all = { ...obj(categoryPrefs) };
  all[category] = { ...obj(all[category]), slack: on };
  return all;
}

/** The stop text for a stored reason, or null. */
export function slackDmStopped(categoryPrefs: unknown): string | null {
  const r = obj(categoryPrefs)[SLACK_DM_STOPPED_KEY];
  return typeof r === 'string' ? (STOP_TEXT[r as SlackDmStopCode] ?? STOP_TEXT.cannot_dm_bot) : null;
}

export function withoutSlackDmStopped(categoryPrefs: unknown): Record<string, unknown> {
  const all = { ...obj(categoryPrefs) };
  delete all[SLACK_DM_STOPPED_KEY];
  return all;
}

/** The method row's summary: "Disabled", "All notifications", "Assignments, Mentions" or "A, B, 3 others". */
export function slackDmSummary(channelPrefs: unknown, categoryPrefs: unknown): string {
  if (!slackDmOn(channelPrefs)) return 'Disabled';
  const on = SLACK_DM_CATEGORIES.filter((c) => slackCategoryOn(categoryPrefs, c.id)).map((c) => c.label);
  if (on.length === 0) return 'Disabled';
  if (on.length === SLACK_DM_CATEGORIES.length) return 'All notifications';
  if (on.length <= 2) return on.join(', ');
  return `${on[0]}, ${on[1]}, ${on.length - 2} ${on.length - 2 === 1 ? 'other' : 'others'}`;
}

/** Bursts: more than this many DMs to one person within a minute become one summary DM. */
export const SLACK_DM_BURST = 10;
export const SLACK_DM_BURST_WINDOW_S = 60;

export const slackDmBurstText = (n: number): string => `You have ${n} more ${n === 1 ? 'notification' : 'notifications'}`;
